import React, { useEffect, useRef, useState } from 'react'
import styles from './styles/carousel_style.module.scss'
import IconButton from '../icon-button/IconButton'
import { getLanguageDir } from '../../../../store/ui/reducers'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'

const ITEM_GAP = 24
const MIN_ITEM_SIZE = 375;
const SCREEN_CUT = 991;
const MIN_DISTANCE = 50;

const Carousel = ({ items, count, showButtons, minItemSize, gap, section, languageDir }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);
    const [visibleItems, setVisibleItems] = useState(count);
    const [basisSize, setBasisSize] = useState(0);
    const [useButtons, setUseButtons] = useState(false);

    const _GAP = gap ? gap : ITEM_GAP;
    
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    const sliderRef = useRef(null);
    const isTouchRef = useRef(false);
    const hasTouchedRef = useRef(false);
    const isScrollingRef = useRef(false);
    const useMouseScrollRef = useRef(false);
    const smoothScrollRef = useRef(false);

    const touchStartXRef = useRef(null);
    const touchEndXRef = useRef(null);
    const basisSizeRef = useRef(basisSize);
    const currentIndexRef = useRef(currentIndex);
    const isRTL = languageDir === 'rtl';

    const calcMeasures = (width) => {
        const _minItemSize = Math.min(width / 1.1, minItemSize ? minItemSize : MIN_ITEM_SIZE);
        const _visibleItems = (count && count * _minItemSize <= width) ? count : Math.round(10 * width / _minItemSize) / 10;

        const gapCount = Math.ceil(_visibleItems - 1);
        const basisSize = (width - _GAP * gapCount)/ _visibleItems
        basisSizeRef.current = basisSize;

        setVisibleItems(_visibleItems);
        setViewportWidth(width);
        setBasisSize(basisSize);
    }

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target.id === 'd-carousel-viewport-id') {
                    const { width } = entry.contentRect;
                    calcMeasures(width);
                }
            }
        })

        if (sliderRef.current) {
            observer.observe(sliderRef.current);
        }

        const resize = () => {
            setScreenSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })
        }

        window.addEventListener("resize", resize);
        return () => {
            console.log('* REMOVE LISTENER RESIZE *')
            if (sliderRef.current) {
                observer.unobserve(sliderRef.current);
            }
            observer.disconnect();

        window.removeEventListener("resize", resize);
        }
    }, [])


    function checkScrollEnd () {
        if (smoothScrollRef.current) return;
        if (sliderRef.current.lastScrollPosition === sliderRef.current.scrollLeft) {
            if (++sliderRef.current.stableFrames > 2) {
                isScrollingRef.current = false;
                if (smoothScrollRef.current === false && isTouchRef.current === false) handleSwipe()
                return;
            }
        } else {
            isScrollingRef.current = true;
            sliderRef.current.stableFrames = 0;
            sliderRef.current.lastScrollPosition = sliderRef.current.scrollLeft;
        }
        requestAnimationFrame(checkScrollEnd);
    }

    const handleScroll = () => {
        if (smoothScrollRef.current || isScrollingRef.current || useMouseScrollRef.current) return;
        sliderRef.current.stableFrames = 0
        requestAnimationFrame(checkScrollEnd);
    }

    useEffect(() => {
        if (sliderRef.current && !useButtons) {
            sliderRef.current.lastScrollPosition = 0
            sliderRef.current.addEventListener('scroll', handleScroll);
        }
        return () => {
            sliderRef.current.removeEventListener('scroll', handleScroll);
        }
    }, [sliderRef.current, useButtons])

    useEffect(() => {
        const useButtons = showButtons === true || showButtons === undefined && screenSize.width >= SCREEN_CUT;
        setUseButtons(useButtons);
    }, [screenSize])


    function smoothScrollTo(element, target, duration = 600) {
        touchStartXRef.current = 0;
        touchEndXRef.current = 0;

        smoothScrollRef.current = true;
        const start = element.scrollLeft;
        const change = target - start;
        const startTime = performance.now();
        const easeInOutQuad = (t) =>  t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        function animateScroll(currentTime) {
            if (isTouchRef.current) {
                smoothScrollRef.current = false
                return
            }
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const ease = easeInOutQuad(progress);

            element.scrollLeft = start + change * ease;

            if (progress < 1) {
                requestAnimationFrame(animateScroll);
            } else {
                smoothScrollRef.current = false
            }
        }

        requestAnimationFrame(animateScroll);
    }


    const handlePrev = (e) => {
        e.preventDefault();
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
    };

    const handleNext = (e) => {
        e.preventDefault();
        setCurrentIndex((prev) =>
            Math.min(prev + 1, items.length - visibleItems)
        );
    };

    const handleTouchStart = (e) => {
        isTouchRef.current =  hasTouchedRef.current = true;
        touchStartXRef.current = e.changedTouches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        isTouchRef.current = false;
        touchEndXRef.current = e.changedTouches[0].clientX;
        handleSwipe();
    };

    const handleMouseDown = (e) => {
        if (hasTouchedRef.current) return;
        useMouseScrollRef.current = true;
        touchStartXRef.current = sliderRef.current.scrollLeft;
    }

    const handleMouseUp = (e) => {
        if (hasTouchedRef.current) return;
        touchEndXRef.current = sliderRef.current.scrollLeft;
        handleSwipe();
    }

    function handleSwipe () {
        if (isScrollingRef.current || smoothScrollRef.current)  return;

        if (sliderRef.current) {
            const distance = touchStartXRef.current - touchEndXRef.current;
            const testDistance = useMouseScrollRef.current || Math.abs(distance) >= MIN_DISTANCE ? 1 : 0;
            const distanceSign = useMouseScrollRef.current ? -Math.sign(distance) : Math.sign(distance)
            const fractionLimit =  distanceSign * (visibleItems - Math.trunc(visibleItems))
            const rawCurrentIndex = sliderRef.current.scrollLeft / (basisSizeRef.current + _GAP)
            const indexFraction = rawCurrentIndex - Math.trunc(rawCurrentIndex);
            const indexDelta = distanceSign * indexFraction - distanceSign;

            const _currentIndex = testDistance ? (indexDelta < fractionLimit ? Math.ceil(rawCurrentIndex) : Math.floor(rawCurrentIndex)) : currentIndexRef.current;

            const scrollDistance = _currentIndex * (basisSizeRef.current + _GAP);
            smoothScrollTo(sliderRef.current, scrollDistance, 450)
            currentIndexRef.current = _currentIndex;
        }
    }

    const displayButton = {...{display: useButtons ? 'flex' : 'none'}}
    const overflowTrack = {...{overflow: useButtons ? 'hidden' : 'auto'}}

    return (
        <div className={styles['d-carousel-container']}
             onTouchStart={!useButtons && handleTouchStart}
             onTouchEnd={!useButtons && handleTouchEnd}
        >
            <div className={styles['d-carousel-buttons-container']}
                 style={displayButton}>
                <div className={styles['d-carousel-buttons']} >
                    <IconButton size-l
                                style-primary
                                img={isRTL ? "chevron_right" : "chevron_left"}
                                disabled={currentIndex === 0}
                                onClick={handlePrev}
                    />
                    <IconButton size-l
                                style-primary
                                img={isRTL ? "chevron_left" : "chevron_right"}
                                disabled={currentIndex >= items.length - visibleItems}
                                onClick={handleNext}
                    />
                </div>
            </div>

            <div id="d-carousel-viewport-id"
                 className={styles['d-carousel-viewport']}
                 style={overflowTrack}
                 ref={sliderRef}
                 onMouseDown={!useButtons && handleMouseDown}
                 onMouseUp={!useButtons && handleMouseUp}
            >
                <div
                    className={styles['d-carousel-track']}
                    style={{
                        columnGap: `${_GAP}px`,
                        ...(useButtons && {transform: `translateX(max(calc(min(${viewportWidth}px, 100%) - 100%), calc(${isRTL ? "" : "-"}${currentIndex} * calc(${basisSize}px + ${_GAP}px))))`})
                    }}
                >
                    {items.map((ItemComponent, index) => (
                        <div key={index} className={styles['d-carousel-item']} style={{flexBasis:`${basisSize}px`}}>
                            {ItemComponent}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        languageDir: getLanguageDir(state)
    }
}

export default injectIntl(connect(mapStateToProps, null)(Carousel));

